import { createReducer } from '@reduxjs/toolkit'
import { put, takeEvery, select } from 'redux-saga/effects'
import {
    patchNode,
    deleteNode,
    selectNodeById,
    selectParentId,
    selectPreviousSiblingId,
    selectNextSiblingId,
    selectBestChildIdFor
} from './node'
import { ROOT_NODE_ID } from '../store/migrations/data-structure-conversion'
import { CURSOR } from './actionTypes'

// Actions

export function startTextEdit() {
    return {
        type: CURSOR.EDITING_TEXT.START
    }
}

export function endTextEdit() {
    return {
        type: CURSOR.EDITING_TEXT.END
    }
}

export function endTextEditNoSave() {
    return {
        type: CURSOR.EDITING_TEXT.END_NO_SAVE
    }
}

export function deleteCursorNode() {
    return {
        type: CURSOR.DELETE_NODE
    }
}

export function moveCursorTo(id) {
    return {
        type: CURSOR.MOVE_CURSOR_TO,
        payload: { id }
    }
}

export function moveCursorToBestChildOrSelf(id) {
    return {
        type: CURSOR.MOVE_CURSOR_TO_BEST_CHILD_OR_SELF,
        payload: { id }
    }
}

// Reducers

const initialState = {
    // cursor
    editingText: false,
    originalText: undefined,
    cursorNodeId: undefined
}

export const cursorReducer = createReducer(initialState, {
    [CURSOR.EDITING_TEXT.START]: (state, action) => {
        const { nodes } = action
        const { cursorNodeId } = state
        if (!nodes[cursorNodeId]) return

        const oldText = nodes[cursorNodeId].text

        state.editingText = true
        state.originalText = oldText
    },
    [CURSOR.UPDATE_ORIGINAL_TEXT]: (state, action) => {
        state.originalText = action.payload
    },
    [CURSOR.EDITING_TEXT.END]: (state) => {
        state.editingText = false
    },
    [CURSOR.EDITING_TEXT.END_NO_SAVE]: (state) => {
        state.editingText = false
    },

    [CURSOR.MOVE_CURSOR_TO]: (state, action) => {
        const id = action.payload.id
        if (!id) return
        else if (id === ROOT_NODE_ID) return
        if (!action.nodes[id]) {
            const fakeState = {
                cursor: state,
                nodes: action.nodes
            }
            state.cursorNodeId = selectBestChildIdFor(ROOT_NODE_ID)(fakeState)
        } else {
            state.cursorNodeId = id
        }
    },
    [CURSOR.MOVE_CURSOR_TO_BEST_CHILD_OR_SELF]: (state, action) => {
        let { id } = action.payload
        if (!id) return
        if (!action.nodes[id]) id = ROOT_NODE_ID

        const fakeState = {
            cursor: state,
            nodes: action.nodes
        }
        const bestChildId = selectBestChildIdFor(id)(fakeState)

        state.cursorNodeId = bestChildId || id
    }
})

// Saga
function* endEditingTextNoSaveSaga(action) {
    const cursorNodeId = yield select(selectCursorNodeId)
    const text = yield select((state) => state.cursor.originalText)

    if (text === '') {
        const node = yield select(selectNodeById(cursorNodeId))
        if (node.childrenIds.length === 0) {
            yield deleteCursorNodeSaga(action)
        } else {
            // TODO move child to this node's place instead
            yield put(patchNode(cursorNodeId, { text }))
        }
    } else {
        yield put(patchNode(cursorNodeId, { text }))
    }
}

function* endEditingTextSaga(action) {
    // yield put(push('/home'))

    const cursorNodeId = yield select(selectCursorNodeId)
    const node = yield select(selectNodeById(cursorNodeId))
    if (node.text === '' && node.childrenIds.length === 0) {
        yield deleteCursorNodeSaga(action)
    }
}

// TODO consider converting this saga to a reducer
function* deleteCursorNodeSaga(_action) {
    const cursorNodeId = yield select(selectCursorNodeId)
    const cursorNode = yield select(selectNodeById(cursorNodeId))
    if (!cursorNode) return
    const parentId = yield select(selectParentId(cursorNodeId))
    const previousSiblingId = yield select(selectPreviousSiblingId(cursorNodeId))
    const nextSiblingId = yield select(selectNextSiblingId(cursorNodeId))

    const nextCursorCandidate = previousSiblingId || nextSiblingId || parentId

    if (nextCursorCandidate === ROOT_NODE_ID) {
        yield put(moveCursorTo(undefined))
    } else {
        yield put(moveCursorTo(nextCursorCandidate))
    }

    yield put(deleteNode(cursorNodeId))
}

export function* cursorSaga() {
    yield takeEvery(CURSOR.EDITING_TEXT.END_NO_SAVE, endEditingTextNoSaveSaga)
    yield takeEvery(CURSOR.EDITING_TEXT.END, endEditingTextSaga)
    yield takeEvery(CURSOR.DELETE_NODE, deleteCursorNodeSaga)
}

// Selectors
export const selectCursorNodeId = (state) => state.cursor.cursorNodeId

export const selectCursorNode = (state) => {
    const cursorNodeId = selectCursorNodeId(state)
    return selectNodeById(cursorNodeId)(state)
}
